import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { ReactComponent as EditIcon } from "assets/icons/edit.svg";
import defaultAvatar from "assets/icons/profile-picture.svg";
import { getImageUrl, ImageSourceType } from "utils/imageUtils";
import "./Avatar.css";

const Avatar = ({ src, alt = "", size = "md", editable = false, onImageChange, className = "", ...rest }) => {
  const fileInputRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(src || defaultAvatar);

  const sizeClasses = {
    sm: "avatar-sm",
    md: "avatar-md",
    lg: "avatar-lg",
    xl: "avatar-xl",
  };

  // Update image source when src prop changes
  useEffect(() => {
    if (!src) {
      setImageSrc(defaultAvatar);
      return;
    }

    // Handle different image source types
    if (typeof src === 'string') {
      // If src is a full URL or data URI, use it directly
      if (src.startsWith('http') || src.startsWith('data:') || src.startsWith('blob:')) {
        setImageSrc(src);
      } 
      // If it's a local path that needs processing
      else {
        const processedUrl = getImageUrl(src, { type: ImageSourceType.LOCAL, path: "admin" });
        setImageSrc(processedUrl);
      }
    } else if (src?.url) {
      // Handle object with URL property
      const processedUrl = getImageUrl(src.url, { type: ImageSourceType.LOCAL, path: "admin" });
      setImageSrc(processedUrl);
    } else {
      setImageSrc(defaultAvatar);
    }
  }, [src]);

  const containerClasses = `profile-avatar-container ${sizeClasses[size] || ""} ${editable ? "editable" : ""} ${className}`.trim();

  const handleDrop = (e) => {
    if (!editable) return;
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith("image/")) {
      const imageUrl = URL.createObjectURL(file);
      setImageSrc(imageUrl);
      onImageChange?.(file);
    }
  };

  const handleDragOver = (e) => {
    if (!editable) return;
    e.preventDefault();
  };

  const handleClick = () => {
    if (editable && fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file?.type.startsWith("image/")) {
      const imageUrl = URL.createObjectURL(file);
      setImageSrc(imageUrl);
      onImageChange?.(file);
    }
  };

  const handleImageError = (e) => {
    e.target.onerror = null;
    if (e.target.src !== defaultAvatar) {
      setImageSrc(defaultAvatar);
    }
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      if (imageSrc && imageSrc.startsWith("blob:")) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageSrc]);

  return (
    <div className={containerClasses} onClick={handleClick} onDrop={handleDrop} onDragOver={handleDragOver} role={editable ? "button" : "img"} aria-label={alt} {...rest}>
      <img src={imageSrc} alt={alt} className="profile-avatar-image" onError={handleImageError} />
      {editable && (
        <>
          <div className="profile-avatar-edit">
            <EditIcon width={12} height={12} />
          </div>
          <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" style={{ display: "none" }} />
        </>
      )}
    </div>
  );
};

Avatar.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  editable: PropTypes.bool,
  onImageChange: PropTypes.func,
  className: PropTypes.string,
};

export default Avatar;
